/**
 * @graphql agg
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';

import { shouldUseAGGActivityV3 } from '../shouldUseAGGActivityV3.ts';

import type {
	RecentListHomeRecentWorkV3Query as RecentListHomeRecentWorkV3Query$data,
	RecentListHomeRecentWorkV3QueryVariables as RecentListHomeRecentWorkV3Query$variables,
} from './__types__/RecentListHomeRecentWorkV3Query.ts';
import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers.ts';

export const useHomeRecentWorkData = ({
	isWhiteboardViewedEnabled,
	isDatabaseEnabled,
	first,
}: {
	isWhiteboardViewedEnabled: boolean;
	isDatabaseEnabled: boolean;
	first?: number;
}) => {
	const { cloudId, isLoggedIn } = useSessionData();

	const { data, loading, error } = useQuery<
		RecentListHomeRecentWorkV3Query$data,
		RecentListHomeRecentWorkV3Query$variables
	>(
		gql`
			query useHomeRecentWorkDataQuery(
				$cloudId: ID!
				$first: Int
				$after: String
				$objectTypes: [String!]
			) {
				activity {
					myActivity {
						all(
							filter: {
								type: AND
								rootContainerIds: [$cloudId]
								arguments: { products: ["CONFLUENCE"], objectTypes: $objectTypes }
							}
							first: $first
							after: $after
						) {
							edges {
								cursor
								node {
									id
									object {
										id
										data {
											... on ConfluencePage {
												id
												title
												type
												subtype
												pageStatus: status
												createdAt
												links {
													webUi
													editUi
												}
												metadata {
													titleEmojiDraft {
														value
													}
													titleEmojiPublished {
														value
													}
												}
												space {
													alias
													id
													key
													name
												}
											}
											... on ConfluenceBlogPost {
												id
												title
												type
												blogStatus: status
												createdAt
												links {
													webUi
													editUi
												}
												metadata {
													titleEmojiDraft {
														value
													}
													titleEmojiPublished {
														value
													}
												}
												space {
													alias
													id
													key
													name
												}
											}
											... on ConfluenceWhiteboard {
												id
												title
												type
												status
												links {
													webUi
												}
												space {
													alias
													id
													key
													name
												}
											}
											... on ConfluenceDatabase {
												id
												title
												type
												status
												links {
													webUi
												}
												space {
													alias
													id
													key
													name
												}
											}
											... on ConfluenceEmbed {
												id
												title
												type
												status
												links {
													webUi
												}
												metadata {
													titleEmojiDraft {
														value
													}
													titleEmojiPublished {
														value
													}
												}
												space {
													alias
													id
													key
													name
												}
											}
										}
									}
									event {
										id
										timestamp
										eventType
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			...getRecentWorkQueryOptions({
				cloudId,
				isLoggedIn,
				isWhiteboardViewedEnabled,
				isDatabaseEnabled,
				first,
			}),
			...(shouldUseAGGActivityV3() && { client: getAGGClient() }),
		},
	);

	return { data, loading, error };
};
